import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '@/hooks/useAuth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useProjectsForSelect } from '@/hooks/useProjectsQuery';
import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';
import { supabase } from '@/integrations/supabase/client';
import { uploadStakeholderFile } from '@/lib/storage';
import { StakeholderFileUpload } from './StakeholderFileUpload';
import type { StakeholderAttachment } from '@/pages/admin/StakeholderManagement';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

const stakeholderSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  email: z.string().trim().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().trim().optional(),
  role: z.string().trim().optional(),
  project_id: z.string().trim().optional(),
  project: z.string().trim().optional(), // Text-only project name
  notes: z.string().trim().optional(),
});

type StakeholderFormData = z.infer<typeof stakeholderSchema>;

interface FileWithPreview {
  file: File;
  preview?: string;
  name: string;
  type: string;
  size: number;
  id: string;
}

interface StakeholderAddDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function StakeholderAddDialog({ isOpen, onClose, onSuccess }: StakeholderAddDialogProps) {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<FileWithPreview[]>([]);
  const { data: projects = [] } = useProjectsForSelect();
  const { getStandardValue, setStandardValue, removeStandardValue } = useLocalStorageManager();
  
  const draftKey = 'stakeholder-add-form-draft';
  const hasLoadedDraftRef = useRef(false);

  const form = useForm<StakeholderFormData>({
    resolver: zodResolver(stakeholderSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      role: '',
      project_id: '',
      project: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (isOpen && !hasLoadedDraftRef.current) {
      const draft = getStandardValue<StakeholderFormData>(draftKey, null as any);
      if (draft) {
        form.reset(draft);
      }
      hasLoadedDraftRef.current = true;
    }
    
    // Reset flag when dialog closes
    if (!isOpen) {
      hasLoadedDraftRef.current = false;
      form.reset();
      setUploadedFiles([]);
      logger.debug('[StakeholderAddDialog] Cleaned up on close');
    }
  }, [isOpen, draftKey, getStandardValue, form]);
  
  // Auto-save draft
  useEffect(() => {
    if (!isOpen) return;
    const subscription = form.watch((values) => {
      setStandardValue(draftKey, values as StakeholderFormData);
    });
    return () => subscription.unsubscribe();
  }, [isOpen, form, draftKey, setStandardValue]);

  const onSubmit = async (data: StakeholderFormData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    logger.log(`[StakeholderAddDialog] Starting submission with ${uploadedFiles.length} files`);
    
    try {
      const stakeholderData = {
        name: data.name,
        email: data.email || null,
        phone: data.phone || null,
        role: data.role || 'Stakeholder',
        project: data.project || null, // Text-only project name
        notes: data.notes || null,
        created_by: user?.id || null,
      };

      const { data: newStakeholder, error: stakeholderError } = await supabase
        .from('stakeholders')
        .insert(stakeholderData)
        .select()
        .single();

      if (stakeholderError) {
        console.error('[StakeholderAddDialog] Stakeholder insert failed:', stakeholderError);
        throw stakeholderError;
      }

      logger.log(`[StakeholderAddDialog] Created stakeholder: ${newStakeholder.id}`);

      await new Promise(resolve => setTimeout(resolve, 150));

      const attachments: StakeholderAttachment[] = [];
      if (uploadedFiles.length > 0) {
        logger.log(`[StakeholderAddDialog] Uploading ${uploadedFiles.length} files`);
        
        for (const [index, fileData] of uploadedFiles.entries()) {
          let retryCount = 0;
          const maxRetries = 2;
          let uploaded = false;

          while (retryCount <= maxRetries && !uploaded) {
            try {
              logger.log(`[StakeholderAddDialog] File ${index + 1}/${uploadedFiles.length}: ${fileData.name} (attempt ${retryCount + 1})`);
              
              const result = await uploadStakeholderFile(newStakeholder.id, fileData.file);
              attachments.push(result);
              uploaded = true;
              
              logger.log(`[StakeholderAddDialog] ✓ Uploaded: ${fileData.name}`);
            } catch (uploadError: any) {
              console.error(`[StakeholderAddDialog] Upload attempt ${retryCount + 1} failed:`, uploadError);
              
              retryCount++;
              if (retryCount <= maxRetries) {
                logger.log(`[StakeholderAddDialog] Retrying after 200ms...`);
                await new Promise(resolve => setTimeout(resolve, 200));
              } else {
                console.error(`[StakeholderAddDialog] ✗ All retries failed for ${fileData.name}`);
                toast.error(`Failed to upload: ${fileData.name}`, {
                  description: uploadError.message || 'Unknown error'
                });
              }
            }
          }
        }

        if (attachments.length > 0) {
          logger.log(`[StakeholderAddDialog] Updating with ${attachments.length} attachments`);
          
          const { error: updateError } = await supabase
            .from('stakeholders')
            .update({ attachments: attachments as any })
            .eq('id', newStakeholder.id);

          if (updateError) {
            console.error('[StakeholderAddDialog] Failed to update attachments:', updateError);
            throw updateError;
          }
        }

        if (attachments.length < uploadedFiles.length) {
          toast.warning(`Uploaded ${attachments.length}/${uploadedFiles.length} files`, {
            description: 'Some uploads failed. Check console for details.'
          });
        }
      }

      if (data.project_id) {
        const { error: assignmentError } = await supabase
          .from('project_stakeholder_assignments')
          .insert({
            project_id: data.project_id,
            stakeholder_id: newStakeholder.id,
            role: data.role || 'Stakeholder',
          });

        if (assignmentError) {
          console.error('[StakeholderAddDialog] Assignment failed:', assignmentError);
          throw assignmentError;
        }
      }

      const successMsg = attachments.length === uploadedFiles.length && uploadedFiles.length > 0
        ? `Stakeholder added with ${attachments.length} files`
        : 'Stakeholder added successfully';
      
      toast.success(successMsg);
      
      removeStandardValue(draftKey);
      form.reset();
      setUploadedFiles([]);
      onSuccess();
      
    } catch (error: any) {
      console.error('[StakeholderAddDialog] Error:', error);
      toast.error('Failed to add stakeholder', {
        description: error.message || 'Unknown error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    logger.debug('[StakeholderAddDialog] Manual close');
    removeStandardValue(draftKey);
    form.reset();
    setUploadedFiles([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent key={isOpen ? 'open' : 'closed'} className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Stakeholder</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter stakeholder name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter email" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter phone number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter role" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="project_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Link to Project</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.project_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="project"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project Name (text only)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter project name if not linking" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter notes about this stakeholder..."
                      className="min-h-[100px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium">Attachments</label>
              <StakeholderFileUpload
                value={uploadedFiles}
                onFilesChange={setUploadedFiles}
                disabled={isSubmitting}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add stakeholder'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}